import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { SectionCard, formatNumber } from '@datahub/design-system'
import { get } from '../../lib/api'
import type { AdminUser } from '../../lib/types'

interface DashboardData {
  responseRateByCadence: Record<string, { invited: number; responded: number; pct: number }>
  overdue: number
  submissionsLast7Days: number
  bouncedContacts: number
  rowsAwaitingCopy: number
  neverResponded: number
}

const CADENCE_LABEL: Record<string, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semi_annual: 'Semi-annual', annual: 'Annual', unknown: 'No cadence',
}

/** RPT-07. */
export function Dashboard({ admin }: { admin: AdminUser }) {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => get<DashboardData>('/admin/reports/dashboard'),
  })

  const tiles = [
    { label: 'Overdue companies', value: data?.overdue ?? 0, to: '/admin/cycles?status=overdue' },
    { label: 'Submissions in the last 7 days', value: data?.submissionsLast7Days ?? 0, to: '/admin/submissions' },
    { label: 'Rows awaiting copy to Core', value: data?.rowsAwaitingCopy ?? 0, to: '/admin/submissions?copied=not_started' },
    { label: 'Bounced contacts', value: data?.bouncedContacts ?? 0, to: '/admin/companies' },
    { label: 'Companies that have never responded', value: data?.neverResponded ?? 0, to: '/admin/companies' },
  ]

  return (
    <div className="flex flex-col bg-background-body" style={{ padding: 20, height: '100%', overflow: 'hidden' }}>
      <div className="flex items-center flex-shrink-0" style={{ height: 58 }}>
        <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px' }}>
          Dashboard
        </h1>
      </div>
      <div className="flex-1 overflow-y-auto bg-background-main" style={{ padding: 20, border: '0.5px solid #4B5563' }}>
        <p className="font-mulish text-text-support" style={{ fontSize: 12, marginBottom: 20 }}>
          Signed in as {admin.displayName ?? admin.email}
          {admin.isBootstrap && ' (default admin)'}
        </p>
        {isLoading ? (
          <p className="font-mulish text-text-support" style={{ fontSize: 12 }}>Loading…</p>
        ) : (
          <div className="grid grid-cols-2 items-start gap-5">
            <div className="flex flex-col gap-5">
              <SectionCard title="Where things stand">
                <div className="flex flex-col">
                  {tiles.map((tile) => (
                    <button
                      key={tile.label}
                      onClick={() => navigate(tile.to)}
                      className="flex items-center text-left hover:bg-background-body"
                      style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563', background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}
                    >
                      <span className="font-mulish text-white" style={{ fontSize: 12, width: '70%' }}>{tile.label}</span>
                      <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(tile.value)}
                      </span>
                    </button>
                  ))}
                </div>
              </SectionCard>
            </div>
            <div className="flex flex-col gap-5">
              <SectionCard title="Response rate by cadence">
                <div className="flex flex-col">
                  {Object.entries(data?.responseRateByCadence ?? {}).length === 0 ? (
                    <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: '12px 20px' }}>
                      No open cycles yet.
                    </p>
                  ) : (
                    Object.entries(data!.responseRateByCadence).map(([cadence, v]) => (
                      <div key={cadence} className="flex items-center" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                        <span className="font-mulish text-white" style={{ fontSize: 12, width: '42%' }}>{CADENCE_LABEL[cadence] ?? cadence}</span>
                        <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>
                          {v.pct}% · {v.responded} of {v.invited} responded
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </SectionCard>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
